import type { WebTrackingAPI } from '@DigitalZoneApp/event-tracking/front';
import { Source } from './types';

export class EventTrackingService {
  constructor(
    private track: WebTrackingAPI['track'],
    private getPathname?: () => string | undefined,
    private getAppSource?: () => string,
    private getUserId?: () => string | undefined,
    private getSourceFn?: () => Source,
  ) {}

  private normalizePathname(pathname: string): string {
    return (
      pathname
        .replace(/^\/[a-z]{2}(-[A-Z]{2})?(\/|$)/, '/')
        .replace(/^\//, '')
        .split('/')[0] || ''
    );
  }

  private getNormalizedPathname(): string | undefined {
    const pathname = this.getPathname?.();
    if (!pathname) return undefined;
    return this.normalizePathname(pathname);
  }

  private getSource(source?: Source): Source {
    if (source) return source;

    if (this.getSourceFn) {
      return this.getSourceFn();
    }

    const normalizedPath = this.getNormalizedPathname();

    if (!normalizedPath || normalizedPath === '') {
      return 'home_page';
    }

    switch (normalizedPath) {
      case '':
      case 'home':
        return 'home_page';
      case 'matches':
        return 'matches_page';
      case 'concerts':
        return 'concerts_page';
      case 'concert':
        return 'concert_page';
      case 'workshops':
        return 'workshops_page';
      case 'event':
        return 'event_page';
      case 'checkout':
        return 'checkout_page';
      case 'summary':
        return 'order_summary_page';
      case 'profile':
        return 'profile_page';
      case 'history':
        return 'history_page';
      case 'international-events':
      case 'international-event':
        return 'international_events_page';
      default:
        return 'navigation';
    }
  }

  private getAppSourceValue(): string {
    return this.getAppSource?.() || 'web';
  }

  private getUserIdValue(): string | undefined {
    return this.getUserId?.();
  }

  page = {
    viewed: ({ pageName, url }: { pageName: string; url: string }) => {
      this.track('page_viewed', {
        page_name: pageName,
        url: url,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
  };

  eventCategory = {
    clicked: ({ categoryId, categoryName }: { categoryId: string; categoryName: string }) => {
      this.track('event_category_clicked', {
        category_id: categoryId,
        category_name: categoryName,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
  };

  event = {
    viewed: ({
      eventId,
      eventName,
      eventDate,
      venue,
      source,
    }: {
      eventId: number;
      eventName: string;
      eventDate: string | null;
      venue: string;
      source?: Source;
    }) => {
      const resolvedSource = this.getSource(source);
      this.track('event_viewed', {
        event_id: eventId,
        event_name: eventName,
        event_date: eventDate,
        venue: venue,
        source: resolvedSource,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
    clicked: ({
      eventId,
      eventName,
      eventDate,
      venue,
      source,
    }: {
      eventId: number | undefined;
      eventName: string;
      eventDate: string | null | undefined;
      venue: string | undefined;
      source?: Source;
    }) => {
      const resolvedSource = this.getSource(source);
      this.track('event_clicked', {
        event_id: eventId,
        event_name: eventName,
        event_date: eventDate,
        venue: venue,
        source: resolvedSource,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
  };

  // International events are display-only (booking happens off-site on
  // PlatinumList), so they get their own event names instead of reusing the
  // domestic `event`/`cart`/`share` funnel — keeps intl activity cleanly
  // separable in analytics.
  internationalEvent = {
    viewed: ({
      eventId,
      eventName,
      eventDate,
      venue,
      source,
    }: {
      eventId: number;
      eventName: string;
      eventDate: string | null;
      venue: string;
      source?: Source;
    }) => {
      const resolvedSource = this.getSource(source);
      this.track('international_event_viewed', {
        event_id: eventId,
        event_name: eventName,
        event_date: eventDate,
        venue: venue,
        source: resolvedSource,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
    clicked: ({
      eventId,
      eventName,
      eventDate,
      venue,
      source,
    }: {
      eventId: number;
      eventName: string;
      eventDate: string | null | undefined;
      venue: string | undefined;
      source?: Source;
    }) => {
      const resolvedSource = this.getSource(source);
      this.track('international_event_clicked', {
        event_id: eventId,
        event_name: eventName,
        event_date: eventDate,
        venue: venue,
        source: resolvedSource,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
    // The "احجز الآن" CTA — the intl equivalent of purchase intent. There is no
    // local cart, so we send the display price point instead of quantity/total.
    bookNowPressed: ({
      eventId,
      eventName,
      startingPrice,
      currency,
    }: {
      eventId: number;
      eventName: string;
      startingPrice: number;
      currency: string;
    }) => {
      this.track('international_book_now_pressed', {
        event_id: eventId,
        event_name: eventName,
        starting_price: startingPrice,
        currency: currency,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
    shared: ({ eventName }: { eventName: string }) => {
      this.track('international_event_shared', {
        page: this.getSource() ?? 'navigation',
        event_name: eventName,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
  };

  seat = {
    selected: ({
      eventId,
      sector,
      gate,
      price,
    }: {
      eventId: number;
      sector: string;
      gate: string;
      price: number;
    }) => {
      this.track('seat_selected', {
        event_id: eventId,
        sector: sector,
        gate: gate,
        price: price,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
    removed: ({
      eventId,
      sector,
      gate,
      price,
    }: {
      eventId: number;
      sector: string;
      gate: string;
      price: number;
    }) => {
      this.track('seat_removed', {
        event_id: eventId,
        sector: sector,
        gate: gate,
        price: price,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
  };

  cart = {
    addToCart: ({
      eventId,
      ticketQuantity,
      totalPrice,
    }: {
      eventId: number;
      ticketQuantity: number;
      totalPrice: number;
    }) => {
      this.track('add_to_cart', {
        event_id: eventId,
        ticket_quantity: ticketQuantity,
        total_price: totalPrice,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
    purchaseTicketPressed: ({
      eventId,
      ticketQuantity,
      totalPrice,
    }: {
      eventId: number;
      ticketQuantity: number;
      totalPrice: number;
    }) => {
      this.track('purchase_ticket_pressed', {
        event_id: eventId,
        ticket_quantity: ticketQuantity,
        total_price: totalPrice,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
  };

  checkout = {
    started: ({
      orderId = 0,
      eventId,
      totalPrice,
      paymentMethod,
    }: {
      orderId?: number;
      eventId: number | undefined;
      totalPrice: number;
      paymentMethod: string;
    }) => {
      this.track('checkout_started', {
        order_id: orderId,
        event_id: eventId,
        total_price: totalPrice,
        payment_method: paymentMethod,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
  };

  order = {
    completed: ({
      orderId,
      eventId,
      totalPrice,
    }: {
      orderId: number;
      eventId: number;
      totalPrice: number;
    }) => {
      this.track('order_completed', {
        order_id: orderId,
        event_id: eventId,
        total_price: totalPrice,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
    failed: ({
      orderId,
      eventId,
      totalPrice,
      orderStatus,
      errorCode,
    }: {
      orderId: number;
      eventId: number;
      totalPrice: number;
      orderStatus: string;
      errorCode: string;
    }) => {
      this.track('order_failed', {
        order_id: orderId,
        event_id: eventId,
        total_price: totalPrice,
        order_status: orderStatus,
        error_code: errorCode,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
    cancelled: ({
      orderId,
      eventId,
      totalPrice,
      orderStatus,
      errorCode,
    }: {
      orderId: number;
      eventId: number;
      totalPrice: number;
      orderStatus: string;
      errorCode: string;
    }) => {
      this.track('order_cancelled', {
        order_id: orderId,
        event_id: eventId,
        total_price: totalPrice,
        order_status: orderStatus,
        error_code: errorCode,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
  };

  eventDay = {
    accessed: ({
      ticketOrderId,
      ticketId,
      eventId,
    }: {
      ticketOrderId: number | undefined;
      ticketId: string | undefined;
      eventId: number | undefined;
    }) => {
      this.track('event_day_accessed', {
        order_id: ticketOrderId,
        ticket_id: ticketId,
        event_id: eventId,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
  };

  login = {
    clicked: ({ source }: { source?: Source }) => {
      const resolvedSource = this.getSource(source);
      this.track('log_in_clicked', {
        app_source: this.getAppSourceValue(),
        source: resolvedSource,
      });
    },
    phoneNumberContinuePressed: ({ phoneNumber }: { phoneNumber: string | undefined }) => {
      this.track('log_in_phone_number_continue_pressed', {
        app_source: this.getAppSourceValue(),
        phone_number: phoneNumber,
      });
    },
    verificationCodeContinuePressed: () => {
      this.track('log_in_verification_code_continue_pressed', {
        app_source: this.getAppSourceValue(),
      });
    },
  };

  profile = {
    pageClicked: () => {
      this.track('profile_page_clicked', {
        user_id: this.getUserIdValue() || '',
        app_source: this.getAppSourceValue(),
      });
    },
    updated: ({ updatedFields }: { updatedFields: string[] | null }) => {
      this.track('profile_updated', {
        user_id: this.getUserIdValue() || '',
        updated_fields: updatedFields,
        app_source: this.getAppSourceValue(),
      });
    },
  };

  orderHistory = {
    clicked: () => {
      this.track('order_history_clicked', {
        user_id: this.getUserIdValue() || '',
        app_source: this.getAppSourceValue(),
      });
    },
  };

  share = {
    buttonClicked: ({
      page,
      typeOfEvent,
      eventName,
    }: {
      page?: string;
      typeOfEvent: string;
      eventName: string;
    }) => {
      const resolvedPage = page ?? this.getSource() ?? 'navigation';
      this.track('share_button_clicked', {
        page: resolvedPage,
        type_of_event: typeOfEvent,
        event_name: eventName,
        app_source: this.getAppSourceValue(),
        user_id: this.getUserIdValue() || '',
      });
    },
  };
}
